import { asOptionalString, asRecord, asString } from '../../dto/common';
import { normalizeUnitCode } from '../../utils/units';
import { roundWarehouseQuantity } from '../../utils/warehouseQuantity';

/**
 * WZ source picker — per WZ line the operator chooses where the goods come
 * from (decision 2026-09-23-wz-source-picker):
 *  - FIFO:  from the warehouse, oldest stock first (default);
 *  - TRUCK: the whole line straight from one delivery (truck or received PZ);
 *  - MIX:   part from that delivery, the rest FIFO.
 * The backend (`wzSourceService`) owns the rules and turns the choice into
 * batch pins; this module only reads the candidate deliveries, keeps the line
 * fields consistent and mirrors the input checks for immediate feedback.
 */
export type WZLineSourceMode = 'FIFO' | 'TRUCK' | 'MIX';

export interface WZLineSourceFields {
  sourceMode?: WZLineSourceMode | null;
  sourceTransportId?: string | null;
  sourcePZDocumentId?: string | null;
  sourceQuantity?: number | null;
}

export interface WZSourceLine extends WZLineSourceFields {
  productVariantId?: string | null;
  quantity?: number | null;
  unit?: string | null;
}

export interface WZSourceQuantitySet {
  pcs: number | null;
  m2: number | null;
  m3: number | null;
}

export interface WZSourceDelivery {
  kind: 'TRANSPORT' | 'PZ';
  id: string;
  /** Truck label (TR-…) or PZ number. */
  label: string | null;
  status: string | null;
  supplierNames: string[];
  supplierOrderNumbers: string[];
  estimatedArrival: string | null;
  receiptDate: string | null;
  /** Received on a PZ and not reserved for anybody else. */
  receivedAvailable: WZSourceQuantitySet;
  /** Still in the truck (not received on a PZ yet). */
  inTransit: WZSourceQuantitySet;
}

/** Candidate deliveries by product variant id. */
export type WZSourceCandidates = Record<string, WZSourceDelivery[]>;

export interface WZLineSourceIssue {
  key: string;
  values?: Record<string, string | number>;
}

const SOURCE_UNITS = ['pcs', 'm2', 'm3'] as const;

function optionalNumber(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function normalizeQuantitySet(raw: unknown): WZSourceQuantitySet {
  const record = asRecord(raw);
  return {
    pcs: optionalNumber(record?.pcs),
    m2: optionalNumber(record?.m2),
    m3: optionalNumber(record?.m3),
  };
}

function asStringList(raw: unknown): string[] {
  return Array.isArray(raw) ? raw.map((value) => asOptionalString(value)).filter((value): value is string => Boolean(value)) : [];
}

function normalizeDelivery(raw: unknown): WZSourceDelivery | null {
  const record = asRecord(raw);
  const id = asOptionalString(record?.id);
  const kind = record?.kind === 'TRANSPORT' || record?.kind === 'PZ' ? record.kind : null;
  if (!record || !id || !kind) return null;
  return {
    kind,
    id,
    label: asOptionalString(record.label) ?? null,
    status: asOptionalString(record.status) ?? null,
    supplierNames: asStringList(record.supplierNames),
    supplierOrderNumbers: asStringList(record.supplierOrderNumbers),
    estimatedArrival: asOptionalString(record.estimatedArrival) ?? null,
    receiptDate: asOptionalString(record.receiptDate) ?? null,
    receivedAvailable: normalizeQuantitySet(record.receivedAvailable),
    inTransit: normalizeQuantitySet(record.inTransit),
  };
}

export function normalizeWZSourceCandidates(raw: unknown): WZSourceCandidates {
  const variants = asRecord(raw)?.variants;
  const result: WZSourceCandidates = {};
  if (!Array.isArray(variants)) return result;
  variants.forEach((entry) => {
    const record = asRecord(entry);
    const variantId = asString(record?.productVariantId);
    if (!variantId) return;
    result[variantId] = (Array.isArray(record?.deliveries) ? record!.deliveries as unknown[] : [])
      .map(normalizeDelivery)
      .filter((delivery): delivery is WZSourceDelivery => delivery !== null);
  });
  return result;
}

export function getWZLineSourceMode(line: WZLineSourceFields): WZLineSourceMode {
  return line.sourceMode === 'TRUCK' || line.sourceMode === 'MIX' ? line.sourceMode : 'FIFO';
}

export function getDeliveryKey(delivery: Pick<WZSourceDelivery, 'kind' | 'id'>): string {
  return `${delivery.kind}:${delivery.id}`;
}

export function getLineDeliveryKey(line: WZLineSourceFields): string | null {
  if (line.sourceTransportId) return `TRANSPORT:${line.sourceTransportId}`;
  if (line.sourcePZDocumentId) return `PZ:${line.sourcePZDocumentId}`;
  return null;
}

export function findLineDelivery(
  line: WZLineSourceFields,
  deliveries: WZSourceDelivery[] | undefined,
): WZSourceDelivery | null {
  const key = getLineDeliveryKey(line);
  return key ? (deliveries || []).find((delivery) => getDeliveryKey(delivery) === key) || null : null;
}

/** Quantity of a delivery set in the line's unit; null when not expressible. */
export function getDeliveryQuantity(set: WZSourceQuantitySet, unit: string | null | undefined): number | null {
  const code = normalizeUnitCode(unit);
  if (!code || !(SOURCE_UNITS as readonly string[]).includes(code)) return null;
  const value = set[code as keyof WZSourceQuantitySet];
  return value === null ? null : roundWarehouseQuantity(Math.max(0, value));
}

/** Everything the delivery can still give: received and free plus still in the truck. */
export function getDeliveryCapacity(delivery: WZSourceDelivery, unit: string | null | undefined): number | null {
  const received = getDeliveryQuantity(delivery.receivedAvailable, unit);
  const inTransit = getDeliveryQuantity(delivery.inTransit, unit);
  if (received === null && inTransit === null) return null;
  return roundWarehouseQuantity((received || 0) + (inTransit || 0));
}

/** Truck part of the line in its own unit (TRUCK = whole line). */
export function getLineTruckQuantity(line: WZSourceLine): number {
  const mode = getWZLineSourceMode(line);
  if (mode === 'TRUCK') return roundWarehouseQuantity(Number(line.quantity) || 0);
  if (mode === 'MIX') return roundWarehouseQuantity(Number(line.sourceQuantity) || 0);
  return 0;
}

export function patchWZLineSourceMode(line: WZSourceLine, mode: WZLineSourceMode): WZLineSourceFields {
  if (mode === 'FIFO') {
    return { sourceMode: 'FIFO', sourceTransportId: null, sourcePZDocumentId: null, sourceQuantity: null };
  }
  const quantity = roundWarehouseQuantity(Number(line.quantity) || 0);
  const current = Number(line.sourceQuantity);
  return {
    sourceMode: mode,
    sourceTransportId: line.sourceTransportId ?? null,
    sourcePZDocumentId: line.sourcePZDocumentId ?? null,
    sourceQuantity: mode === 'TRUCK'
      ? quantity
      : (Number.isFinite(current) && current > 0 && current < quantity ? current : null),
  };
}

export function patchWZLineSourceDelivery(delivery: Pick<WZSourceDelivery, 'kind' | 'id'> | null): WZLineSourceFields {
  return {
    sourceTransportId: delivery?.kind === 'TRANSPORT' ? delivery.id : null,
    sourcePZDocumentId: delivery?.kind === 'PZ' ? delivery.id : null,
  };
}

/**
 * Immediate feedback mirroring the backend checks. Only an incomplete choice
 * or more than the delivery carries at all is an error; the rest is left to
 * the release flow (goods still in the truck become a pending cost there).
 */
export function validateWZLineSource(
  line: WZSourceLine,
  deliveries: WZSourceDelivery[] | undefined,
): WZLineSourceIssue | null {
  const mode = getWZLineSourceMode(line);
  if (mode === 'FIFO') return null;
  if (!line.productVariantId) return { key: 'wz:source.errors.catalogProductRequired' };
  const quantity = roundWarehouseQuantity(Number(line.quantity) || 0);
  if (mode === 'MIX') {
    const truck = Number(line.sourceQuantity);
    if (!Number.isFinite(truck) || truck <= 0 || truck >= quantity) {
      return { key: 'wz:source.errors.mixQuantityRange' };
    }
  }
  if (!getLineDeliveryKey(line)) return { key: 'wz:source.errors.deliveryRequired' };
  const delivery = findLineDelivery(line, deliveries);
  if (!delivery) return deliveries ? { key: 'wz:source.errors.deliveryUnavailable' } : null;
  const capacity = getDeliveryCapacity(delivery, line.unit);
  const requested = getLineTruckQuantity(line);
  if (capacity !== null && requested > capacity + 0.0005) {
    return { key: 'wz:source.errors.exceedsDelivery', values: { available: capacity } };
  }
  return null;
}

/** The four fields the WZ save sends for a line (FIFO clears the rest). */
export function buildWZLineSourcePayload(line: WZSourceLine): Required<WZLineSourceFields> {
  const mode = getWZLineSourceMode(line);
  if (mode === 'FIFO') {
    return { sourceMode: 'FIFO', sourceTransportId: null, sourcePZDocumentId: null, sourceQuantity: null };
  }
  return {
    sourceMode: mode,
    sourceTransportId: line.sourceTransportId || null,
    sourcePZDocumentId: line.sourceTransportId ? null : (line.sourcePZDocumentId || null),
    sourceQuantity: getLineTruckQuantity(line),
  };
}
